const MAX_POLLS = 30
const DEFAULT_POLL_INTERVAL = 1000
const PENDING_MESSAGE = 'Sending test…'
const ACCEPTED_MESSAGE = 'Test SMS accepted by Twilio.'
const FAILURE_MESSAGE = 'Test SMS could not be sent. Check the number and Twilio configuration, then try again.'

export default class SmsTestStatus extends HTMLElement {
  connectedCallback () {
    this.form = this.querySelector('[data-sms-test-form]')
    this.submitButton = this.querySelector('[data-sms-test-submit]')
    this.result = this.querySelector('[data-sms-test-result]')
    this.healthButton = this.querySelector('[data-sms-health-check]')
    this.pollInterval = DEFAULT_POLL_INTERVAL
    this.abortControllers = new Set()
    this.active = true

    this.form?.addEventListener('submit', this.onSubmit)
    this.healthButton?.addEventListener('click', this.onHealthCheck)
    document.addEventListener('turbo:before-cache', this.cancel)
  }

  disconnectedCallback () {
    this.form?.removeEventListener('submit', this.onSubmit)
    this.healthButton?.removeEventListener('click', this.onHealthCheck)
    document.removeEventListener('turbo:before-cache', this.cancel)
    this.cancel()
  }

  onSubmit = async (event) => {
    event.preventDefault()
    if (this.submitButton?.disabled) return

    this.clearError()
    this.setPending(true)
    this.setResult(PENDING_MESSAGE)

    try {
      const response = await this.fetch(this.form.action, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form)
      })

      if (response.status !== 202) throw new Error('test request rejected')

      const payload = await response.json()
      if (!payload.status_url) throw new Error('missing status URL')

      const statusUrl = new URL(payload.status_url, window.location.origin)
      this.pollCount = 0
      this.schedulePoll(`${statusUrl.pathname}${statusUrl.search}`)
    } catch (error) {
      if (error.name !== 'AbortError') this.finishFailure()
    }
  }

  onHealthCheck = async () => {
    if (this.healthButton.disabled) return

    this.healthButton.disabled = true
    this.toggle(this.querySelector('[data-health-idle]'), false)
    this.toggle(this.querySelector('[data-health-pending]'), true)

    try {
      await this.fetch(this.dataset.healthUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      })
      if (this.active) window.location.reload()
    } catch (error) {
      if (error.name !== 'AbortError' && this.active) window.location.reload()
    }
  }

  schedulePoll (url) {
    if (!this.active) return
    this.timer = window.setTimeout(() => this.poll(url), this.pollInterval)
  }

  async poll (url) {
    if (!this.active) return
    this.pollCount += 1

    try {
      const response = await this.fetch(url, { headers: { Accept: 'application/json' } })
      if (!response.ok) throw new Error('status request failed')

      const payload = await response.json()
      if (payload.state === 'accepted') {
        this.finish(ACCEPTED_MESSAGE)
      } else if (payload.state === 'failed') {
        this.finishFailure()
      } else if (payload.state === 'pending' && this.pollCount < MAX_POLLS) {
        this.schedulePoll(url)
      } else {
        this.finishFailure()
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.finishFailure()
    }
  }

  async fetch (url, options = {}) {
    const controller = new AbortController()
    this.abortControllers.add(controller)

    try {
      return await window.fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal })
    } finally {
      this.abortControllers.delete(controller)
    }
  }

  finishFailure () {
    this.showError()
    this.finish(FAILURE_MESSAGE)
  }

  finish (message) {
    window.clearTimeout(this.timer)
    this.timer = null
    this.setResult(message)
    this.setPending(false)
  }

  setPending (pending) {
    if (!this.submitButton) return

    this.submitButton.disabled = pending
    this.submitButton.setAttribute('aria-busy', pending.toString())
    this.toggle(this.querySelector('[data-submit-idle]'), !pending)
    this.toggle(this.querySelector('[data-submit-pending]'), pending)
  }

  setResult (message) {
    if (this.result) this.result.textContent = message
  }

  showError () {
    const fields = this.querySelectorAll('[data-sms-country], [data-sms-national-number]')
    fields.forEach((field) => field.setAttribute('aria-invalid', 'true'))
  }

  clearError () {
    this.querySelectorAll('[data-sms-country], [data-sms-national-number]').forEach((field) => {
      field.removeAttribute('aria-invalid')
    })
  }

  toggle (element, visible) {
    if (!element) return
    element.classList.toggle('hidden', !visible)
    element.classList.toggle('flex', visible)
  }

  cancel = () => {
    this.active = false
    window.clearTimeout(this.timer)
    this.timer = null
    this.abortControllers?.forEach((controller) => controller.abort())
    this.abortControllers?.clear()
  }
}

export { MAX_POLLS }
